import React, { useState, useEffect, useCallback } from 'react';
import {
  getPortalAccess, enablePortalAccess, revokePortalAccess, rotatePortalAccess,
  adminListClientMessages, adminSendClientMessage,
} from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Copy, Trash2, RefreshCw, Link as LinkIcon, MessageSquare, Send, Loader2, ExternalLink, Mail } from 'lucide-react';

const PortalAccessDialog = ({ open, onOpenChange, client }) => {
  const [tab, setTab] = useState('access');
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const portalBase = `${window.location.origin}/c/`;

  const load = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const [a, m] = await Promise.all([
        getPortalAccess(client.id),
        adminListClientMessages(client.id).catch(() => ({ data: { items: [] } })),
      ]);
      setAccess(a.data);
      setMessages(m.data?.items || []);
    } catch (e) {
      toast.error('Error cargando portal');
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleEnable = async () => {
    try {
      const r = await enablePortalAccess(client.id);
      setAccess({ ...access, active: true, access_token: r.data.access_token, login_count: 0 });
      toast.success('Acceso al portal activado');
    } catch (e) {
      toast.error('No se pudo activar');
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('¿Revocar el acceso? El cliente ya no podrá entrar.')) return;
    try {
      await revokePortalAccess(client.id);
      setAccess({ ...access, active: false });
      toast.success('Acceso revocado');
    } catch (e) {
      toast.error('Error al revocar');
    }
  };

  const handleRotate = async () => {
    if (!window.confirm('¿Generar un nuevo enlace? El anterior dejará de funcionar.')) return;
    try {
      const r = await rotatePortalAccess(client.id);
      setAccess({ ...access, active: true, access_token: r.data.access_token });
      toast.success('Nuevo enlace generado');
    } catch (e) {
      toast.error('Error al rotar token');
    }
  };

  const copyUrl = () => {
    if (!access?.access_token) return;
    const url = portalBase + access.access_token;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  const sendEmail = () => {
    if (!access?.access_token || !client?.email) return;
    const url = portalBase + access.access_token;
    const subject = encodeURIComponent('Tu portal de cliente');
    const body = encodeURIComponent(
      `Hola ${client.name},\n\nTe envío el enlace a tu portal de cliente, donde podrás consultar facturas, presupuestos y comunicarte conmigo:\n\n${url}\n\nGracias.`,
    );
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
  };

  const handleSendMsg = async () => {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    try {
      await adminSendClientMessage(client.id, content);
      setDraft('');
      toast.success('Mensaje enviado');
      // refresh
      const r = await adminListClientMessages(client.id);
      setMessages(r.data?.items || []);
    } catch (e) {
      toast.error('No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const portalUrl = access?.access_token ? portalBase + access.access_token : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="portal-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-[#FF6B4A]" />
            Portal del Cliente · {client?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-stone-100">
          {['access', 'messages'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-2 text-sm font-medium border-b-2 ${
                tab === t ? 'border-[#FF6B4A] text-stone-900' : 'border-transparent text-stone-500'
              }`}
              data-testid={`portal-dialog-tab-${t}`}
            >
              {t === 'access' ? 'Acceso' : `Mensajes (${messages.length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B4A]" /></div>
        ) : tab === 'access' ? (
          <div className="space-y-4 py-2">
            {!access?.active ? (
              <div className="bg-stone-50 rounded-xl p-5 text-center">
                <p className="text-sm text-stone-600 mb-3">Este cliente todavía no tiene acceso al portal.</p>
                <Button
                  onClick={handleEnable}
                  className="bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white"
                  data-testid="enable-portal-btn"
                >
                  Activar portal y generar enlace
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-stone-600 uppercase">Enlace de acceso</label>
                  <div className="flex gap-2 mt-1">
                    <Input value={portalUrl} readOnly className="font-mono text-xs" data-testid="portal-url-input" />
                    <Button variant="outline" size="sm" onClick={copyUrl} data-testid="copy-portal-url-btn">
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => window.open(portalUrl, '_blank')}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-stone-400 mt-1">Comparte este enlace con tu cliente. Cualquiera con el enlace puede acceder al portal.</p>
                </div>

                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-[10px] uppercase text-stone-500 font-semibold">Accesos</p>
                    <p className="text-2xl font-bold text-stone-900">{access.login_count || 0}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-[10px] uppercase text-stone-500 font-semibold">Primer acceso</p>
                    <p className="text-sm font-semibold text-stone-700">{access.first_login_at ? new Date(access.first_login_at).toLocaleDateString('es-ES') : '—'}</p>
                  </div>
                  <div className="bg-stone-50 rounded-xl p-3">
                    <p className="text-[10px] uppercase text-stone-500 font-semibold">Último acceso</p>
                    <p className="text-sm font-semibold text-stone-700">{access.last_login_at ? new Date(access.last_login_at).toLocaleDateString('es-ES') : '—'}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-100">
                  {client?.email && (
                    <Button variant="outline" size="sm" onClick={sendEmail} data-testid="email-portal-btn">
                      <Mail className="w-4 h-4 mr-1" /> Enviar por email
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={handleRotate} data-testid="rotate-portal-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Nuevo enlace
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleRevoke} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="revoke-portal-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Revocar acceso
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="max-h-[300px] overflow-y-auto bg-stone-50 rounded-xl p-3 space-y-2">
              {messages.length === 0 ? (
                <p className="text-center text-sm text-stone-400 py-6">Sin mensajes todavía.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                      m.sender_type === 'user' ? 'bg-[#FF6B4A] text-white' : 'bg-white border border-stone-200 text-stone-800'
                    }`}>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      <p className={`text-[10px] mt-1 ${m.sender_type === 'user' ? 'text-white/70' : 'text-stone-400'}`}>
                        {new Date(m.created_at).toLocaleString('es-ES')}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex gap-2">
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Responder al cliente..." rows={2} className="flex-1" data-testid="admin-message-input" />
              <Button onClick={handleSendMsg} disabled={sending || !draft.trim()} className="bg-[#FF6B4A] text-white self-end" data-testid="admin-send-message-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PortalAccessDialog;
